import React from 'react';
import { TabV2Props } from 'flow/components/pbx/v2/misc/TabsV2';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import TabLabel from './TabLabel';
import RelatedTabLabel from './RelatedTabLabel';
import ScrollablePillTabs from 'flow/components/ui-components/ScrollablePillTabs';

export type TicketPageTabKey =
    | 'CONVERSATION'
    | 'CONTACT'
    | 'RELATED'
    | 'TICKET_DETAILS';

const ALL_TAB_KEYS: TicketPageTabKey[] = [
    'CONVERSATION',
    'TICKET_DETAILS',
    'CONTACT',
    'RELATED',
];

interface Props {
    ticketKey: TicketKey;
    selectedTab: TicketPageTabKey;
    onSelectTab: (tab: TicketPageTabKey) => void;
    /** Subset / order of tabs to render. Defaults to all four. */
    tabKeys?: TicketPageTabKey[];
    /** Render the details-panel dock toggle after the pills. */
    showDock?: boolean;
    onToggleDock?: () => void;
    /** Lets the parent move focus back to the dock toggle. */
    dockToggleRef?: React.RefObject<HTMLButtonElement>;
}

const TabsTicket = ({
    ticketKey,
    selectedTab,
    onSelectTab,
    tabKeys = ALL_TAB_KEYS,
    showDock = false,
    onToggleDock,
    dockToggleRef,
}: Props) => {
    const { i18next } = useAppContext();

    const labelByKey: Record<TicketPageTabKey, TabV2Props['label']> = {
        CONVERSATION: (
            <TabLabel
                iconKey="chat"
                text={i18next.t('tickets.conversation.singular')}
            />
        ),
        CONTACT: (
            <TabLabel
                iconKey="account"
                text={i18next.t('cti.wrapper.type.label.contact')}
            />
        ),
        RELATED: <RelatedTabLabel ticketKey={ticketKey} />,
        TICKET_DETAILS: (
            <TabLabel iconKey="subject" text={i18next.t('ticket.details')} />
        ),
    };

    const tabs: TabV2Props[] = tabKeys.map((key) => ({
        key,
        label: labelByKey[key],
    }));

    const handleClick = (tab: TabV2Props) => {
        onSelectTab(tab.key as TicketPageTabKey);
    };

    const dockToggleLabel = i18next.t(
        'tickets.detailsPanel.toggle',
        'Open details panel',
    );

    // IconButton: `tooltipLabel` names the icon-only button and shows the
    // hover hint; BaseButton brings the focus ring and darken-10 hover.
    const dockToggle =
        showDock && onToggleDock ? (
            <IconButton
                ref={dockToggleRef}
                type="button"
                onClick={onToggleDock}
                tooltipLabel={dockToggleLabel}
                className="size-8 shrink-0 rounded-full bg-gray-200 text-gray-600"
            >
                <Icon
                    iconKey="right_panel"
                    size="1.25rem"
                    className="rtl:scale-x-[-1]"
                />
            </IconButton>
        ) : null;

    return (
        <ScrollablePillTabs
            selectedKey={selectedTab}
            tabs={tabs}
            onClick={handleClick}
            trailing={dockToggle}
        />
    );
};

export default React.memo(TabsTicket);
